import { Platform } from 'react-native';

const DEFAULT_SEPARATOR = ' ';

/**
 * Options for building an accessibility hint with error information.
 */
export interface BuildAccessibilityHintOptions {
    /**
     * The error message to prepend to the hint.
     * When present, the hint becomes "{errorPrefix}{errorMessage}{separator}{baseHint}".
     */
    errorMessage?: string | null;

    /**
     * Prefix for error messages (e.g., "Error: ").
     * Defaults to empty string (no prefix) for backward compatibility.
     * Consumers who want the "Error: " prefix should pass it explicitly.
     */
    errorPrefix?: string;

    /**
     * The base accessibility hint for the element (e.g., "Opens selection modal").
     */
    baseHint?: string;

    /**
     * Separator between error message and base hint.
     * Defaults to " " (space) to match original MADS TextInput behavior.
     */
    separator?: string;
}

/**
 * Builds an accessibility hint that includes error information for native platforms.
 *
 * ## Platform-specific behavior
 *
 * **iOS/Android**: Prepends error message to the hint so screen readers announce it
 * when the element receives focus. Format: "{errorPrefix}{errorMessage}{separator}{baseHint}"
 *
 * **Web**: Returns undefined. Web uses aria-describedby to reference error elements
 * by ID, which is the idiomatic pattern for web accessibility. Using both
 * accessibilityHint and aria-describedby would cause double announcements.
 *
 * ## Error Prefix
 *
 * By default, no prefix is added to error messages for backward compatibility with
 * existing MADS TextInput behavior. Consumers who want the "Error: " prefix pattern
 * should pass `errorPrefix: "Error: "` explicitly.
 *
 * @example
 * // No error - just base hint
 * buildAccessibilityHint({ baseHint: 'Opens selection modal' })
 * // => 'Opens selection modal'
 *
 * // Error without prefix (MADS default)
 * buildAccessibilityHint({ errorMessage: 'Email is required', baseHint: 'Enter your email' })
 * // => 'Email is required Enter your email'
 *
 * // Error with prefix (recommended for clarity)
 * buildAccessibilityHint({
 *   errorMessage: 'Email is required',
 *   errorPrefix: 'Error: ',
 *   baseHint: 'Enter your email'
 * })
 * // => 'Error: Email is required Enter your email'
 *
 * // Error only, no base hint
 * buildAccessibilityHint({ errorMessage: 'Invalid format', errorPrefix: 'Error: ' })
 * // => 'Error: Invalid format'
 */
export function buildAccessibilityHint({
    errorMessage,
    errorPrefix = '',
    baseHint,
    separator = DEFAULT_SEPARATOR,
}: BuildAccessibilityHintOptions): string | undefined {
    // Web uses aria-describedby exclusively for descriptions/errors.
    // Returning a hint here would cause duplicate announcements.
    if (Platform.OS === 'web') {
        return undefined;
    }

    const trimmedError = errorMessage?.trim();
    const trimmedHint = baseHint?.trim();

    if (trimmedError && trimmedHint) {
        return `${errorPrefix}${trimmedError}${separator}${trimmedHint}`;
    }

    if (trimmedError) {
        return `${errorPrefix}${trimmedError}`;
    }

    return trimmedHint;
}
